import {
  EXAM_TYPES,
  type ExamType,
  toExamTypes,
  toGqlExamType,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { EpocusSeedKind, EpocusSeedSource } from "elephant-web/gql";
import { useCallback, useEffect, useMemo } from "react";
import {
  type EpocusSeedableStudiesQuery,
  type EpocusSeedLibraryQuery,
  useClearEpocusSeededDemoStudiesMutation,
  useEpocusSeedableStudiesLazyQuery,
  useEpocusSeedLibraryLazyQuery,
  useSeedEpocusDemoStudiesMutation,
} from "./__generated__/epocus-settings-operations.gql";

export type SeedableStudy =
  EpocusSeedableStudiesQuery["epocusSeedableStudies"][number];

export type SeedLibraryCounts = { stills: number; cines: number };

export type SeedLibrary = {
  version: string | null;
  byExamType: Partial<Record<ExamType, SeedLibraryCounts>>;
  attribution: EpocusSeedLibraryQuery["epocusSeedLibrary"]["attribution"];
};

export type SeedRequest = {
  count: number;
  kind: EpocusSeedKind;
  source: EpocusSeedSource;
  examTypes: readonly ExamType[];
};

export type SeedDemoStudiesResult =
  | {
      outcome: "seeded";
      count: number;
      withCines: number;
      examTypes: readonly ExamType[];
    }
  | { outcome: "empty" }
  | { outcome: "unavailable" };

export type ClearDemoStudiesResult =
  | { outcome: "cleared"; count: number }
  | { outcome: "unavailable" };

export const studiesOfKind = (
  pool: readonly SeedableStudy[],
  kind: EpocusSeedKind,
): readonly SeedableStudy[] => {
  switch (kind) {
    case EpocusSeedKind.Cines:
      return pool.filter((study) => study.cineCount > 0);
    case EpocusSeedKind.Stills:
      return pool.filter((study) => study.cineCount === 0);
    default:
      return pool;
  }
};

export const libraryHasKind = (
  counts: SeedLibraryCounts | undefined,
  kind: EpocusSeedKind,
): boolean => {
  if (!counts) return false;
  switch (kind) {
    case EpocusSeedKind.Cines:
      return counts.cines > 0;
    case EpocusSeedKind.Stills:
      return counts.stills > 0;
    default:
      return counts.stills + counts.cines > 0;
  }
};

export const toSeedLibrary = (
  summary: EpocusSeedLibraryQuery["epocusSeedLibrary"] | undefined,
): SeedLibrary | null => {
  if (!summary?.available) return null;
  const byExamType: Partial<Record<ExamType, SeedLibraryCounts>> = {};
  for (const entry of summary.byExamType) {
    const [examType] = toExamTypes([entry.examType]);
    if (examType) {
      byExamType[examType] = { stills: entry.stills, cines: entry.cines };
    }
  }
  return {
    version: summary.version ?? null,
    byExamType,
    attribution: summary.attribution,
  };
};

export const useSeedDemoStudies = (programId: string | null) => {
  const [fetchPool, { data: poolData, loading: poolLoading }] =
    useEpocusSeedableStudiesLazyQuery();
  const [fetchLibrary, { data: libraryData, loading: libraryLoading }] =
    useEpocusSeedLibraryLazyQuery();
  const [seedDemoStudies] = useSeedEpocusDemoStudiesMutation();
  const [clearDemoStudies] = useClearEpocusSeededDemoStudiesMutation();

  useEffect(() => {
    if (programId) {
      fetchPool({ variables: { programId } });
      fetchLibrary({ variables: { programId } });
    }
  }, [programId, fetchPool, fetchLibrary]);

  const pool = poolData?.epocusSeedableStudies ?? [];
  const library = useMemo(
    () => toSeedLibrary(libraryData?.epocusSeedLibrary),
    [libraryData],
  );

  const seed = useCallback(
    async (
      deviceSerialNumber: string | null,
      { count, kind, source, examTypes }: SeedRequest,
    ): Promise<SeedDemoStudiesResult> => {
      if (!programId) return { outcome: "unavailable" };
      const fromLibrary = source === EpocusSeedSource.Library;
      if (!fromLibrary && studiesOfKind(pool, kind).length === 0) {
        return { outcome: "empty" };
      }
      if (
        fromLibrary &&
        !examTypes.some((examType) =>
          libraryHasKind(library?.byExamType[examType], kind),
        )
      ) {
        return { outcome: "empty" };
      }

      const { data } = await seedDemoStudies({
        variables: {
          programId,
          count,
          kind,
          deviceSerialNumber,
          source,
          examTypes: fromLibrary ? examTypes.map(toGqlExamType) : null,
        },
      });
      const seeded = data?.seedEpocusDemoStudies ?? [];
      const seededExamTypes = toExamTypes(
        seeded.flatMap((study) => (study.examType ? [study.examType] : [])),
      );

      return {
        outcome: "seeded",
        count: seeded.length,
        withCines: seeded.filter((study) => study.cineCount > 0).length,
        examTypes: EXAM_TYPES.filter((examType) =>
          seededExamTypes.includes(examType),
        ),
      };
    },
    [programId, pool, library, seedDemoStudies],
  );

  const clear = useCallback(async (): Promise<ClearDemoStudiesResult> => {
    if (!programId) return { outcome: "unavailable" };
    const { data } = await clearDemoStudies({ variables: { programId } });
    return {
      outcome: "cleared",
      count: data?.clearEpocusSeededDemoStudies.clearedCount ?? 0,
    };
  }, [programId, clearDemoStudies]);

  return {
    seed,
    clear,
    pool,
    library,
    loading: poolLoading || libraryLoading,
  };
};
